import ListSubheader from '@material-ui/core/ListSubheader';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import _ from 'lodash';
import React from 'react';
import {
  ListChildComponentProps,
  VariableSizeList,
} from 'react-window';

const LISTBOX_PADDING = 8; // px

function renderRow({ index, style, data }: ListChildComponentProps) {
  const element = _.get(data, index) as unknown;
  if (
    !_.isObject(element) ||
    !React.isValidElement<{ style?: unknown }>(element)
  ) {
    return null;
  }

  return React.cloneElement(element, {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>(
  (props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <div ref={ref} {...props} {...outerProps} />;
  }
);

function useResetCache(data: unknown) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

/**
 * Allows React to render only the list elements that are viewable through
 * the scroll container. It stops laggy component updates from occurring due
 * to trying to render too many elements at once.
 */
const VirtualizedList = React.forwardRef<HTMLDivElement>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    // true if the viewport size is sm (small) or larger
    const sizeSmOrLarger = useMediaQuery(theme.breakpoints.up('sm'), {
      noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = sizeSmOrLarger ? 42 : 48;

    const getChildSize = (child: React.ReactNode) => {
      if (
        React.isValidElement(child) &&
        child.type === ListSubheader
      ) {
        return 48;
      }

      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={index => getChildSize(itemData[index])}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

export default VirtualizedList as React.ComponentType<
  React.HTMLAttributes<HTMLElement>
>;
